import {
  Account,
  CustomAnalyticsEvent,
  ExternalPixelEvent,
  TeamMember,
} from '@superfunnel/types';
import {
  QueryDocumentSnapshot,
  and,
  collection,
  collectionGroup,
  doc,
  documentId,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { LAST_USED_ACCOUNT_STORAGE_KEY } from '../constants/storage';
import { db } from '../Firebase';
import { chunk, emptyConverter } from '../utils';
import { AuthContext } from './AuthContext';

interface UserContextProps {
  selectedAccount?: QueryDocumentSnapshot<Account>;
  accounts?: QueryDocumentSnapshot<Account>[];
  refetchAccounts: () => Promise<void>;
  createOAuthRequest: (
    provider: 'google' | 'facebook',
    from: string,
    scopes: string[],
  ) => Promise<string>;
  analyticsCustomEvents?: CustomAnalyticsEvent[];
  analyticsExternalPixelEvents?: ExternalPixelEvent[];
  selectedAccountMember?: TeamMember;
  role?: string;
}

export const UserContext = createContext<UserContextProps>({
  refetchAccounts: async () => {},
  createOAuthRequest: async () => '',
});

export function UserContextProvider(props: any) {
  const location = useLocation();

  const { firebaseUser, user } = useContext(AuthContext);

  const [accountsWithMembers, setAccountsWithMembers] = useState<
    {
      member: TeamMember;
      account: QueryDocumentSnapshot<Account>;
    }[]
  >();
  const accounts = useMemo(
    () => accountsWithMembers?.map((a) => a.account),
    [accountsWithMembers],
  );
  const selectedAccountWithMember = useMemo(() => {
    const slug = location.pathname.split('/')[2];
    return accountsWithMembers?.find((a) => a.account.data().slug === slug);
  }, [accountsWithMembers, location.pathname]);

  const selectedAccount = useMemo(
    () => selectedAccountWithMember?.account,
    [selectedAccountWithMember],
  );

  const selectedAccountMember = useMemo(
    () => selectedAccountWithMember?.member,
    [selectedAccountWithMember],
  );

  const role = useMemo(
    () => selectedAccountMember?.role,
    [selectedAccountMember],
  );

  const analyticsCustomEvents = useMemo(() => {
    return selectedAccount?.data().analyticsCustomEvents;
  }, [selectedAccount]);

  const analyticsExternalPixelEvents = useMemo(() => {
    return selectedAccount?.data().analyticsExternalPixelEvents;
  }, [selectedAccount]);

  const fetchAccounts = useCallback(async (userId: string) => {
    const teamsColRef = collectionGroup(db, 'team');
    const teams = await getDocs(
      query(
        teamsColRef,
        and(where('userId', '==', userId), where('status', '==', 'approved')),
      ),
    );

    let accounts: {
      account: QueryDocumentSnapshot<Account>;
      member: TeamMember;
    }[] = [];
    const teamsChunks = chunk(teams.docs, 10);
    for (const teamsChunk of teamsChunks) {
      const teamIds = teamsChunk
        .map((t) => t.ref.parent.parent?.id)
        .filter((x) => !!x);
      const accountsChunk = await getDocs(
        query(
          collection(db, 'accounts').withConverter(emptyConverter<Account>()),
          where(documentId(), 'in', teamIds),
          where('status', '==', 'active'),
        ),
      );

      accounts = [
        ...accounts,
        ...accountsChunk.docs.map((d) => ({
          account: d,
          member: teamsChunk
            .find((t) => t.ref.parent.parent?.id === d.id)
            ?.data() as TeamMember,
        })),
      ];
    }

    setAccountsWithMembers(accounts);

    return accounts;
  }, []);

  const fetchAllAccounts = useCallback(async () => {
    const accounts = await getDocs(
      query(
        collection(db, 'accounts').withConverter(emptyConverter<Account>()),
        where('status', '==', 'active'),
      ),
    );

    setAccountsWithMembers(
      accounts.docs.map((d) => ({
        account: d,
        member: {
          createdAt: new Date(),
          role: 'admin',
          status: 'approved',
          userId: firebaseUser?.uid || '',
        },
      })),
    );

    return accounts.docs;
  }, [firebaseUser?.uid]);

  const refetchAccounts = useCallback(async () => {
    if (!firebaseUser || user === undefined) {
      setAccountsWithMembers(undefined);
      return;
    }

    if (user?.isSupportAdmin) {
      await fetchAllAccounts();
    } else {
      await fetchAccounts(firebaseUser.uid);
    }
  }, [fetchAccounts, fetchAllAccounts, firebaseUser, user]);

  useEffect(() => {
    refetchAccounts();
  }, [firebaseUser, user, refetchAccounts]);

  useEffect(() => {
    if (selectedAccount) {
      localStorage.setItem(
        LAST_USED_ACCOUNT_STORAGE_KEY,
        selectedAccount?.data().slug || '',
      );
    } else {
      localStorage.removeItem(LAST_USED_ACCOUNT_STORAGE_KEY);
    }
  }, [selectedAccount]);

  const createOAuthRequest = useCallback(
    async (provider: 'google' | 'facebook', from: string, scopes: string[]) => {
      if (!selectedAccount || !firebaseUser) {
        console.error(
          'selectedAccount',
          selectedAccount,
          'firebaseUser',
          firebaseUser,
        );
        throw new Error('failed to create request');
      }

      const newReqRef = doc(
        collection(db, 'accounts', selectedAccount.id, 'oauthRequests'),
      );
      await setDoc(newReqRef, {
        accountId: selectedAccount.id,
        userId: firebaseUser.uid,
        requestId: newReqRef.id,
        from,
        scopes,
        provider,
      });

      return newReqRef.id;
    },
    [selectedAccount, firebaseUser],
  );

  return (
    <UserContext.Provider
      value={{
        accounts,
        selectedAccount,
        selectedAccountMember,
        role,
        refetchAccounts,
        createOAuthRequest,
        analyticsCustomEvents,
        analyticsExternalPixelEvents,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  return useContext(UserContext);
}
